import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import EmailVerification from '../components/auth/EmailVerification';
import './AuthPages.css';

const ForgotPasswordPage = () => {
  const { t } = useTranslation(['auth', 'validation']);
  const navigate = useNavigate();
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const pageTitle = step === 'request'
    ? t('auth.forgotPassword.title')
    : t('auth.forgotPassword.resetTitle');

  const handleEmailVerificationSuccess = async () => {
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/auth/forgot-password', { email: email.trim() });
      setStep('reset-password');
      setSuccess(response.data.message || t('auth.forgotPassword.success.codeSentIfExists'));
    } catch (requestError) {
      setStep('request');
      setError(requestError.response?.data?.message || t('auth.forgotPassword.error.requestFailed'));
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError(t('validation:email.required'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t('validation:email.invalid'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/auth/forgot-password', { email: email.trim() });

      if (response.data.requiresEmailVerification) {
        setStep('verify-email');
        setSuccess(response.data.message || t('auth.forgotPassword.success.verifyEmailRequired'));
      } else {
        setStep('reset-password');
        setSuccess(response.data.message || t('auth.forgotPassword.success.codeSentIfExists'));
      }
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('auth.forgotPassword.error.requestFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!verificationCode.trim() || verificationCode.trim().length !== 6) {
      setError(t('auth.forgotPassword.validation.invalidCode'));
      return;
    }

    if (!newPassword) {
      setError(t('validation:password.required'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('validation:password.minLength', 'Le mot de passe doit contenir au moins 6 caractères.'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('validation:confirmPassword.mismatch', 'Les mots de passe ne correspondent pas.'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/auth/reset-password', {
        email: email.trim(),
        verificationCode: verificationCode.trim(),
        newPassword
      });

      setSuccess(response.data.message || t('auth.forgotPassword.success.resetCompleted'));
      setTimeout(() => navigate('/login'), 1200);
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('auth.forgotPassword.error.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verify-email') {
    return (
      <Container component="main" maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
        {success && <Alert severity="info" sx={{ mb: 2 }}>{success}</Alert>}
        <EmailVerification
          email={email.trim()}
          autoLoginOnSuccess={false}
          onVerificationSuccess={handleEmailVerificationSuccess}
        />
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            className="auth-page-header"
            sx={{
              width: 'calc(100% + 32px)',
              margin: { xs: '-8px -16px 20px -16px', sm: '-16px -32px 20px -32px' },
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography component="h1" sx={{ fontWeight: 600, fontSize: { xs: '1.3rem', sm: '1.5rem' }, width: '100%', textAlign: 'center' }}>
              {pageTitle}
            </Typography>
            <Typography variant="body2" sx={{ display: 'block', width: '100%', textAlign: 'center', color: '#ffffff', mt: 1 }}>
              <Link component={RouterLink} to="/login" variant="body2" sx={{ color: '#ffffff', textDecoration: 'underline' }}>
                {t('auth.forgotPassword.backToLogin')}
              </Link>
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>}

          {step === 'request' ? (
            <Box component="form" onSubmit={handleRequestReset} noValidate sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {t('auth.forgotPassword.requestDescription')}
              </Typography>

              <TextField
                margin="dense"
                required
                fullWidth
                id="email"
                label={t('auth.forgotPassword.emailLabel')}
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <Button type="submit" fullWidth variant="contained" disabled={loading} className="auth-submit-button">
                {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.forgotPassword.sendCode')}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleResetPassword} noValidate sx={{ width: '100%', mt: 2 }}>
              <TextField
                margin="dense"
                fullWidth
                label={t('auth.forgotPassword.emailLabel')}
                value={email}
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                margin="dense"
                required
                fullWidth
                label={t('auth.forgotPassword.codeLabel')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <TextField
                margin="dense"
                required
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                label={t('auth.forgotPassword.newPasswordLabel')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowNewPassword((prev) => !prev)} edge="end" disabled={loading} size="small">
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  )
                }}
              />

              <TextField
                margin="dense"
                required
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                label={t('auth.forgotPassword.confirmNewPasswordLabel')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end" disabled={loading} size="small">
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  )
                }}
              />

              <Button type="submit" fullWidth variant="contained" disabled={loading} className="auth-submit-button">
                {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.forgotPassword.submitReset')}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPasswordPage;